import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
    Box, Typography, Grid, Stack,
    CircularProgress,
    IconButton, Avatar, useTheme,
    List, ListItemAvatar, ListItemText, ListItemButton,
    Tooltip,
    Card,
    Fade
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import ForumIcon from "@mui/icons-material/Forum";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import GroupsIcon from "@mui/icons-material/Groups";
import { motion } from "framer-motion";

import api from "../api/axios";
import useChat from "../hooks/useChat";

import ChatWindow from "../components/ChatWindow";
import ChatInputComponent from "../components/ChatInputComponent";
import InvitarUsuarioModal from "../components/InvitarUsuarioModal";
import ChatOptionsMenu from "../components/ChatOptionsMenu";
import ChatSettingsModal from "../components/ChatSettingsModal";

const MotionBox = motion(Box);

export default function Mensajes() {
    const theme = useTheme();
    const navigate = useNavigate();
    const token = localStorage.getItem("token") || "";

    const { myNombre, myEmail } = useMemo(() => {
        try {
            const u = JSON.parse(localStorage.getItem("user") || "null");
            const email = u?.email || localStorage.getItem("user_email") || "";
            return {
                myNombre: u?.nombre || u?.username || localStorage.getItem("user_nombre") || email.split('@')[0] || "",
                myEmail: email.toLowerCase(),
            };
        } catch {
            const email = localStorage.getItem("user_email") || "";
            return {
                myNombre: localStorage.getItem("user_nombre") || email.split('@')[0] || "",
                myEmail: email.toLowerCase(),
            };
        }
    }, []);

    const [salas, setSalas] = useState([]);
    const [selectedSala, setSelectedSala] = useState(null);
    const [loadingSalas, setLoadingSalas] = useState(true);

    const [miembros, setMiembros] = useState([]);
    const [permisos, setPermisos] = useState({
        idusuario: null,
        es_admin: false,
        muteado: false,
        bloqueado: false,
    });

    const [mostrarInvitar, setMostrarInvitar] = useState(false);
    const [openConfig, setOpenConfig] = useState(false);

    // Carga salas SOLO una vez (y NO pisa selectedSala si ya existe)
    useEffect(() => {
        let active = true;

        (async () => {
            try {
                const { data } = await api.get("/salas/usuario");
                if (!active) return;

                const list = Array.isArray(data) ? data : [];
                setSalas(list);

                // importantísimo: no cambiar la sala si ya hay una seleccionada
                setSelectedSala((prev) => prev ?? (list[0] || null));
            } catch (e) {
                if (e?.response?.status === 401) navigate("/login");
            } finally {
                if (active) setLoadingSalas(false);
            }
        })();

        return () => {
            active = false;
        };
    }, [navigate]);

    const salaId = selectedSala?.idsala ?? null;

    // Chat (WS + mensajes)
    const chat = useChat(salaId);

    // refreshMiembros (existe y se usa en varios sitios)
    const refreshMiembros = useCallback(async () => {
        if (!salaId) {
            setMiembros([]);
            setPermisos({
                idusuario: null,
                es_admin: false,
                muteado: false,
                bloqueado: false,
            });
            return;
        }

        try {
            const { data } = await api.get(`/salas/miembros/${salaId}`);
            const lista = Array.isArray(data) ? data : [];

            setMiembros(lista);

            const yo = lista.find(
                (m) => (m.email || "").toLowerCase() === (myEmail || "").toLowerCase()
            );

            setPermisos({
                idusuario: yo?.idusuario ?? null,
                es_admin: !!yo?.es_admin,
                muteado: !!yo?.muteado,
                bloqueado: !!yo?.bloqueado,
            });
        } catch {
            setMiembros([]);
            setPermisos({
                idusuario: null,
                es_admin: false,
                muteado: false,
                bloqueado: false,
            });
        }
    }, [salaId, myEmail]);

    // refresca miembros cuando cambia la sala
    useEffect(() => {
        refreshMiembros();
    }, [refreshMiembros]);

    const abandonarSala = useCallback(async () => {
        if (!selectedSala) return;
        if (!window.confirm("¿Seguro que deseas abandonar la sala?")) return;

        try {
            await api.post(`/salas/${selectedSala.idsala}/abandonar`);
            const { data } = await api.get("/salas/usuario");
            const list = Array.isArray(data) ? data : [];
            setSalas(list);
            setSelectedSala(list[0] || null);
        } catch {}
    }, [selectedSala]);

    const abrirConfig = useCallback(async () => {
        await refreshMiembros();
        setOpenConfig(true);
    }, [refreshMiembros]);

    if (loadingSalas) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '80vh', gap: 2 }}>
                <CircularProgress size={60} thickness={4} />
                <Typography variant="body1" sx={{ fontWeight: 600, opacity: 0.6 }}>
                    Cargando tus conversaciones...
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ p: { xs: 1, md: 2 }, width: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* Header */}
            <Fade in timeout={500}>
                <Box sx={{ mb: 4 }}>
                    <Typography variant="h3" sx={{ fontWeight: 950, color: 'primary.main', display: 'flex', alignItems: 'center', gap: 2 }}>
                        <ForumIcon sx={{ fontSize: 40 }} /> Mis Mensajes
                    </Typography>
                    <Typography variant="body1" sx={{ opacity: 0.7, fontWeight: 500 }}>
                        Visualiza y gestiona tus conversaciones con otros usuarios de forma rápida y sencilla.
                    </Typography>
                </Box>
            </Fade>

            <Card sx={{
                height: 'calc(100vh - 220px)',
                borderRadius: 4,
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
                border: '1px solid',
                borderColor: 'divider'
            }}>
                <Grid container spacing={0} sx={{ flex: 1, overflow: 'hidden' }}>
                    {/* Lista Salas */}
                    <Grid item xs={12} md={3} sx={{ bgcolor: 'background.paper', borderRight: '1px solid', borderColor: 'divider', height: '100%', display: 'flex', flexDirection: 'column' }}>
                        <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'rgba(0,0,0,0.01)' }}>
                            <Typography variant="h6" sx={{ fontWeight: 800 }}>Chats</Typography>
                        </Box>
                        <List sx={{ flex: 1, overflowY: 'auto', py: 0 }}>
                            {salas.length === 0 ? (
                                <Box sx={{ p: 4, textAlign: 'center', opacity: 0.5 }}>
                                    <GroupsIcon sx={{ fontSize: 48, mb: 1 }} />
                                    <Typography variant="body2">No tienes salas activas</Typography>
                                </Box>
                            ) : (
                                salas.map((s) => (
                                    <ListItemButton
                                        key={s.idsala}
                                        selected={salaId === s.idsala}
                                        onClick={() => setSelectedSala(s)}
                                        sx={{
                                            py: 2,
                                            borderBottom: '1px solid',
                                            borderColor: 'rgba(0,0,0,0.05)',
                                            '&.Mui-selected': {
                                                bgcolor: alpha(theme.palette.primary.main, 0.1),
                                                '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.15) },
                                                borderLeft: '4px solid',
                                                borderColor: 'primary.main'
                                            }
                                        }}
                                    >
                                        <ListItemAvatar>
                                            <Avatar sx={{ bgcolor: salaId === s.idsala ? 'primary.main' : 'rgba(0,0,0,0.1)', color: salaId === s.idsala ? 'white' : 'text.primary' }}>
                                                {s.nombre.charAt(0).toUpperCase()}
                                            </Avatar>
                                        </ListItemAvatar>
                                        <ListItemText
                                            primary={<Typography sx={{ fontWeight: 700 }}>{s.nombre}</Typography>}
                                            secondary={<Typography variant="caption" sx={{ opacity: 0.7 }} noWrap>{s.descripcion || 'Sin descripción'}</Typography>}
                                        />
                                    </ListItemButton>
                                ))
                            )}
                        </List>
                    </Grid>

                    {/* Chat */}
                    <Grid item xs={12} md={9} sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: 'rgba(0,0,0,0.02)', flex: 1, minWidth: 0 }}>
                        {!salaId ? (
                            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', opacity: 0.4 }}>
                                <ForumIcon sx={{ fontSize: 100, mb: 2 }} />
                                <Typography variant="h5" sx={{ fontWeight: 700 }}>Selecciona una sala para empezar</Typography>
                            </Box>
                        ) : (
                            <>
                                {/* Header Chat */}
                                <Box sx={{
                                    p: 2,
                                    bgcolor: 'background.paper',
                                    borderBottom: '1px solid',
                                    borderColor: 'divider',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    boxShadow: '0 2px 4px rgba(0,0,0,0.02)'
                                }}>
                                    <Stack direction="row" spacing={2} alignItems="center" sx={{ minWidth: 0 }}>
                                        <Avatar sx={{ bgcolor: 'primary.main' }}>{selectedSala?.nombre?.charAt(0).toUpperCase()}</Avatar>
                                        <Box sx={{ minWidth: 0 }}>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 800 }} noWrap>
                                                {selectedSala?.nombre}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <Box component="span" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'success.main', display: 'inline-block' }} />
                                                Chat activo
                                            </Typography>
                                        </Box>
                                    </Stack>

                                    <Stack direction="row" spacing={1}>
                                        <Tooltip title="Invitar usuarios">
                                            <IconButton color="primary" onClick={() => setMostrarInvitar(true)}>
                                                <PersonAddIcon />
                                            </IconButton>
                                        </Tooltip>
                                        <ChatOptionsMenu onConfig={abrirConfig} onLeave={abandonarSala} />
                                    </Stack>
                                </Box>

                                {/* Mensajes */}
                                <Box sx={{ flex: 1, overflow: 'hidden', p: 0, position: 'relative', width: '100%', display: 'flex', flexDirection: 'column' }}>
                                    <ChatWindow
                                        mensajes={chat.mensajes}
                                        myNombre={myNombre}
                                        myEmail={myEmail}
                                        onEdit={chat.editMensaje}
                                        onDelete={chat.deleteMensaje}
                                        readByMap={{}}
                                    />
                                </Box>

                                {/* Input */}
                                <Box sx={{ p: 2, bgcolor: 'background.paper', borderTop: '1px solid', borderColor: 'divider' }}>
                                    <ChatInputComponent
                                        onSend={chat.enviarMensaje}
                                        onAttach={chat.enviarAdjunto}
                                        disabled={permisos.muteado || permisos.bloqueado}
                                    />
                                    {(permisos.muteado || permisos.bloqueado) && (
                                        <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block', textAlign: 'center', fontWeight: 700 }}>
                                            {permisos.bloqueado ? "Has sido bloqueado en esta sala." : "Has sido muteado en esta sala."}
                                        </Typography>
                                    )}
                                </Box>
                            </>
                        )}
                    </Grid>
                </Grid>
            </Card>

            {mostrarInvitar && (
                <InvitarUsuarioModal
                    salaId={salaId}
                    onClose={() => setMostrarInvitar(false)}
                />
            )}

            <ChatSettingsModal
                open={openConfig}
                onClose={() => setOpenConfig(false)}
                miembros={miembros}
                onAbandonarSala={abandonarSala}
                salaId={salaId}
                onRefresh={refreshMiembros}
                currentUserId={permisos?.idusuario}
            />
        </Box>
    );
}